import type { Vec2 } from './vec'
import type { Tile } from './types'
import type { ContentId } from '../content/schema'

// The command queue is the only way intent enters the sim. Render and input
// append typed commands to one ordered FIFO; the tick drains it in phase 1.
// They never mutate sim state directly. Commands are plain data, so a queued
// command that outlives a tick is part of the serialized state.

/** A no-op marker. Used to exercise queue draining. */
export interface NoopCommand {
  type: 'noop'
}

/** Spawn a hauling colonist and register it in the labor pool. */
export interface SpawnColonistCommand {
  type: 'spawn_colonist'
  unitId: number
  pos: Vec2
  /** Movement speed, world units per second. */
  speed: number
  /** Body half-extent, used for non-penetration. */
  radius: number
}

/**
 * Place a building of content `id` on grid tile `tile`. The target is a tile
 * index, never a world or pixel coordinate: the sim converts tile to world with
 * fixed layout constants, so the placement is viewport-independent. The drain
 * validates the id, the tile bounds, and tile occupancy before any mutation and
 * rejects an invalid placement as a no-op (it never throws).
 */
export interface PlaceBuildingCommand {
  type: 'place_building'
  id: ContentId
  tile: Tile
}

/**
 * Assign labor to a chain: set chain `chain`'s target headcount to `count`, the
 * ABSOLUTE number of colonists the shared-pool arbiter should draw to it (not a
 * delta). Absolute-target semantics make reassignment idempotent — reassigning a
 * chain is just setting a new target — so a replayed command log converges to the
 * same allocation. `chain` indexes `state.chains`; the drain rejects an
 * out-of-bounds chain or a non-finite/negative/fractional count as a no-op.
 */
export interface AssignLaborCommand {
  type: 'assign_labor'
  chain: number
  count: number
}

/** The command union. New player actions add variants here. */
export type Command =
  NoopCommand | SpawnColonistCommand | PlaceBuildingCommand | AssignLaborCommand

/** Append a command to the ordered queue. */
export function enqueue(queue: Command[], command: Command): void {
  queue.push(command)
}

/** Build a spawn-colonist command with default speed and radius. */
export function spawnColonist(
  unitId: number,
  pos: Vec2,
  speed = 120,
  radius = 5,
): SpawnColonistCommand {
  return { type: 'spawn_colonist', unitId, pos, speed, radius }
}

/** Build a place-building command targeting a grid tile. */
export function placeBuilding(id: ContentId, tile: Tile): PlaceBuildingCommand {
  return { type: 'place_building', id, tile: { tx: tile.tx, ty: tile.ty } }
}

/** Build an assign-labor command setting chain `chain`'s absolute target to
 * `count`. */
export function assignLabor(chain: number, count: number): AssignLaborCommand {
  return { type: 'assign_labor', chain, count }
}
